/* eslint-disable @next/next/no-img-element */

import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { cn } from '@/utils';
import { buildGumletUrl } from '@/utils/gumlet';

type Props = {
  images: string[];
  onSelect?: (index: number) => void;
};

export type BackgroundCarouselHandle = {
  scrollTo: (index: number) => void;
  selectedIndex: number;
};

const BackgroundCarousel = forwardRef<BackgroundCarouselHandle, Props>(
  ({ images, onSelect }, ref) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: images.length > 1 }, [
      Autoplay({ delay: 5000, stopOnInteraction: true }),
    ]);
    const [selectedIndex, setSelectedIndex] = useState(0);

    const handleSelect = useCallback(() => {
      if (!emblaApi) return;
      setSelectedIndex(emblaApi.selectedScrollSnap());
    }, [emblaApi]);

    useEffect(() => {
      onSelect?.(selectedIndex);
    }, [onSelect, selectedIndex]);

    useImperativeHandle(
      ref,
      () => ({
        scrollTo: (index: number) => emblaApi?.scrollTo(index),
        selectedIndex,
      }),
      [emblaApi, selectedIndex],
    );

    useEffect(() => {
      if (!emblaApi) return;
      handleSelect();
      emblaApi.on('select', handleSelect).on('reInit', handleSelect);
    }, [emblaApi, handleSelect]);

    if (images.length === 0) {
      return null;
    }

    return (
      <div ref={emblaRef} className="absolute inset-0 overflow-hidden">
        <div className="flex h-full">
          {images.map((item, idx) => {
            const isLcp = idx === 0;
            const optimizedSrc = buildGumletUrl(item, { width: 1920, quality: 80 });
            return (
              <div key={`${item}-${idx}`} className="relative min-w-0 shrink-0 grow-0 basis-full">
                <img
                  alt=""
                  src={optimizedSrc}
                  width={1920}
                  height={1080}
                  loading={isLcp ? 'eager' : 'lazy'}
                  decoding={isLcp ? 'sync' : 'async'}
                  fetchPriority={isLcp ? 'high' : 'low'}
                  sizes="100vw"
                  className="h-full w-full object-cover"
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);

BackgroundCarousel.displayName = 'BackgroundCarousel';

type CarouselDotsProps = {
  count: number;
  selectedIndex: number;
  onSelect: (index: number) => void;
  className?: string;
};

export const CarouselDots = ({ count, selectedIndex, onSelect, className }: CarouselDotsProps) => {
  if (count <= 1) {
    return null;
  }

  return (
    <div className={cn('flex items-center justify-center gap-2', className)}>
      {Array.from({ length: count }, (_, index) => (
        <button
          key={index}
          type="button"
          aria-label={`Go to slide ${index + 1}`}
          aria-current={index === selectedIndex ? 'true' : undefined}
          onClick={() => onSelect(index)}
          className={cn(
            'h-2 w-2 rounded-full transition-all duration-300',
            index === selectedIndex ? 'h-2.5 w-2.5 bg-white' : 'bg-white/45 hover:bg-white/70',
          )}
        />
      ))}
    </div>
  );
};

export default BackgroundCarousel;
